import type { CSSProperties } from 'react';

import styles from './Skeleton.module.css';

type Size = number | string;

interface SkeletonProps {
  /** 폭 — px(number) 또는 CSS 값(%, rem 등). 미지정 시 부모 폭을 채움 */
  width?: Size;
  /** 높이 — 기본 14px */
  height?: Size;
  /** 모서리 반경. circle이면 무시 */
  radius?: Size;
  /** 원형(아바타/썸네일 자리표시) */
  circle?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * 단일 shimmer 블록. 로딩 중 실제 요소(텍스트/썸네일/버튼)의 자리를 그대로 흉내낸다.
 * "불러오는 중..." 같은 텍스트 대신 항상 이 목업을 쓴다.
 */
export function Skeleton({ width, height = 14, radius, circle, className, style }: SkeletonProps) {
  const classes = [styles.skel, circle ? styles.circle : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <span
      aria-hidden="true"
      className={classes}
      style={{ width, height, borderRadius: circle ? undefined : radius, ...style }}
    />
  );
}

interface SkeletonTextProps {
  /** 줄 수 — 기본 3 */
  lines?: number;
  /** 줄 간격(px) — 기본 8 */
  gap?: number;
  /** 마지막 줄 폭(문장 끝을 흉내) — 기본 60% */
  lastWidth?: Size;
  /** 각 줄 높이(px) — 기본 12 */
  lineHeight?: number;
  className?: string;
}

/** 여러 줄 텍스트 자리표시 (문단/설명 로딩용) */
export function SkeletonText({
  lines = 3,
  gap = 8,
  lastWidth = '60%',
  lineHeight = 12,
  className,
}: SkeletonTextProps) {
  const classes = [styles.textStack, className ?? ''].filter(Boolean).join(' ');
  return (
    <span aria-hidden="true" className={classes} style={{ gap }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} height={lineHeight} width={i === lines - 1 ? lastWidth : '100%'} />
      ))}
    </span>
  );
}

interface SkeletonPageProps {
  /** 자리표시 행 수 — 기본 6 */
  rows?: number;
}

/**
 * 라우트 전환/전체 화면 로딩용 페이지 스켈레톤.
 * lazy 라우트의 Suspense fallback, 인증 확인 대기 등 "화면 전체가 아직 없음" 상태에 쓴다.
 */
export function SkeletonPage({ rows = 6 }: SkeletonPageProps) {
  return (
    <div className={styles.page} aria-busy="true">
      <Skeleton width="38%" height={28} radius={8} />
      <Skeleton width="60%" height={14} />
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={styles.pageRow}>
          <Skeleton circle width={40} height={40} />
          <div style={{ flex: 1 }}>
            <SkeletonText lines={2} lastWidth="45%" />
          </div>
          <Skeleton width={72} height={24} radius={8} />
        </div>
      ))}
    </div>
  );
}
